import invariant from 'invariant';

import { ScriptLoadTimeoutError } from 'Lib/errors';
import { omit } from 'Lib/utils/lodashUtils';

function injectScript(
  url: string,
  id: string,
  onload: HTMLScriptElement['onload'],
  onerror: HTMLScriptElement['onerror'],
  ontimeout: VoidFunction,
  options: Record<string, any>
) {
  const existingScript = document.querySelector(`script[id='${id}']`);

  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.src = options.qs ? `${url}?${new URLSearchParams(options.qs).toString()}` : url;
  script.id = id;

  script.async = true;
  script.defer = true;
  // should be lowercase in HTML script
  // @ts-expect-error https://github.com/facebook/react/issues/14829
  script.crossorigin = 'anonymous';

  script.onload = onload;
  script.onerror = onerror;

  Object.entries(omit(options, ['qs', 'timeout', 'preferHead'])).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });

  const { body, head } = document;
  if (body && head) {
    if (options.preferHead) {
      head.appendChild(script);
    } else {
      const target = body.getElementsByTagName('script')[0];
      if (!target) {
        head.appendChild(script);
      } else {
        target.parentNode?.insertBefore(script, target);
      }
    }

    setTimeout(ontimeout, options.timeout || 30000);
  }
}

// helper function for smarter script loading with timeout and retry
// differs from JSONP only in that it doesn't expect a callback call
export default function loadScript(
  url: string,
  id: string,
  options: Record<string, any> = {}
): Promise<any> {
  let resolved = false;

  invariant(id, 'Must specify unique script ID');

  return new Promise((resolve: (value: void | PromiseLike<void>) => void, reject) => {
    const scriptOnlineListener = () => {
      if (!resolved) {
        const newOptions = options;
        injectScript(url, id, handleLoad, handleError, handleTimeout, newOptions);
      }
    };

    function handleLoad() {
      if (!resolved) {
        resolved = true;
        resolve();
      }
    }

    function handleError() {
      if (!resolved) {
        resolved = true;
        reject(new Error(`Error fetching script ${id}`));
      }
    }

    function handleTimeout() {
      if (!resolved) {
        reject(new ScriptLoadTimeoutError());
      }
    }

    injectScript(url, id, handleLoad, handleError, handleTimeout, options);

    // https://caniuse.com/#feat=online-status
    if (!navigator?.onLine) {
      window.addEventListener('online', scriptOnlineListener, { once: true, passive: true });
    }
  });
}
